/**
 * Tableau triable avec tri asc/desc, colonnes redimensionnables,
 * visibilite des colonnes configurable, en-tete sticky,
 * et persistance des largeurs dans localStorage.
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from "react";
import useIsMobile from "../utils/useIsMobile";
import "./SortableTable.css";

export default function SortableTable({
  columns,
  rows,
  renderRow,
  sortBy,
  onSort,
  emptyMessage = "Aucune donnée.",
  storageKey,
}) {
  const [sortDir, setSortDir] = useState("asc");
  const [hiddenCols, setHiddenCols] = useState(() => {
    if (!storageKey) return {};
    try {
      return JSON.parse(localStorage.getItem(storageKey + "_hidden")) || {};
    } catch { return {}; }
  });
  const [colWidths, setColWidths] = useState(() => {
    if (!storageKey) return null;
    try {
      return JSON.parse(localStorage.getItem(storageKey + "_widths")) || null;
    } catch { return null; }
  });
  const [showColMenu, setShowColMenu] = useState(false);
  const [initialized, setInitialized] = useState(colWidths !== null);
  const menuRef = useRef(null);
  const theadRef = useRef(null);
  const isMobile = useIsMobile();

  const visibleColumns = useMemo(
    () => columns.filter((col) => !hiddenCols[col.key]),
    [columns, hiddenCols]
  );

  // On first render with rows, capture auto widths and lock to fixed layout
  useEffect(() => {
    // Sur mobile le tableau defile horizontalement a largeur naturelle :
    // ni capture ni ecriture, pour ne pas ecraser les largeurs du poste fixe.
    if (isMobile || initialized || !theadRef.current || rows.length === 0) return;
    // Wait one frame for the browser to compute auto layout
    requestAnimationFrame(() => {
      if (!theadRef.current) return;
      const ths = theadRef.current.querySelectorAll("th");
      const widths = {};
      let idx = 0;
      for (const col of visibleColumns) {
        if (ths[idx]) {
          widths[col.key] = Math.round(ths[idx].getBoundingClientRect().width);
        }
        idx++;
      }
      setColWidths(widths);
      setInitialized(true);
      if (storageKey) {
        localStorage.setItem(storageKey + "_widths", JSON.stringify(widths));
      }
    });
  }, [rows, initialized, visibleColumns, storageKey, isMobile]);

  // Persist hidden cols
  useEffect(() => {
    if (storageKey) {
      localStorage.setItem(storageKey + "_hidden", JSON.stringify(hiddenCols));
    }
  }, [hiddenCols, storageKey]);

  // Close menu on outside click
  useEffect(() => {
    if (!showColMenu) return;
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowColMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("touchstart", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("touchstart", handleClick);
    };
  }, [showColMenu]);

  function handleSort(key) {
    if (!onSort) return;
    if (sortBy === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortDir("asc");
      onSort(key);
    }
  }

  const sortedRows = useMemo(() => {
    if (sortDir === "desc" && sortBy) {
      return [...rows].reverse();
    }
    return rows;
  }, [rows, sortDir, sortBy]);

  function toggleColumn(key) {
    setHiddenCols((prev) => ({ ...prev, [key]: !prev[key] }));
    setColWidths((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }

  function saveWidths(widths) {
    if (storageKey) {
      localStorage.setItem(storageKey + "_widths", JSON.stringify(widths));
    }
  }

  const startResize = useCallback((e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const th = e.target.closest("th");
    if (!th) return;

    // Ensure we have widths captured
    if (!colWidths) {
      const ths = theadRef.current?.querySelectorAll("th");
      if (!ths) return;
      const widths = {};
      let idx = 0;
      for (const col of visibleColumns) {
        if (ths[idx]) widths[col.key] = Math.round(ths[idx].getBoundingClientRect().width);
        idx++;
      }
      setColWidths(widths);
      setInitialized(true);
    }

    const startX = e.clientX;
    const startWidth = th.getBoundingClientRect().width;

    function onMouseMove(ev) {
      const delta = ev.clientX - startX;
      const newWidth = Math.max(30, Math.round(startWidth + delta));
      setColWidths((prev) => {
        const next = { ...prev, [colKey]: newWidth };
        return next;
      });
    }

    function onMouseUp() {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      // Persist after resize
      setColWidths((prev) => {
        saveWidths(prev);
        return prev;
      });
    }

    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }, [colWidths, visibleColumns, storageKey]);

  // Les largeurs memorisees ont ete mesurees sur un ecran large : les
  // reappliquer sur un telephone rendrait le tableau illisible.
  const isFixed = !isMobile && colWidths !== null;

  function thStyle(key) {
    if (!isFixed || !colWidths[key]) return undefined;
    const w = colWidths[key];
    return { width: w, minWidth: w, maxWidth: w };
  }

  return (
    <div className="table-container">
      <div className="table-toolbar">
        <div className="col-toggle-wrapper" ref={menuRef}>
          <button
            className="btn-col-toggle"
            onClick={() => setShowColMenu((v) => !v)}
            title="Afficher / masquer des colonnes"
          >
            &#x2699; Colonnes
          </button>
          {showColMenu && (
            <div className="col-menu">
              {columns.map((col) =>
                col.label ? (
                  <label key={col.key} className="col-menu-item">
                    <input
                      type="checkbox"
                      checked={!hiddenCols[col.key]}
                      onChange={() => toggleColumn(col.key)}
                    />
                    {col.label}
                  </label>
                ) : null
              )}
            </div>
          )}
        </div>
      </div>
      <div className="table-scroll">
        <table
          className="sortable-table"
          style={isFixed ? { tableLayout: "fixed" } : undefined}
        >
          <thead ref={theadRef}>
            <tr>
              {visibleColumns.map((col) => (
                <th
                  key={col.key}
                  className={col.sortable ? "sortable" : ""}
                  style={thStyle(col.key)}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <span className="th-content">
                    {col.label}
                    {sortBy === col.key && (
                      <span className="sort-indicator">
                        {sortDir === "asc" ? " \u25B2" : " \u25BC"}
                      </span>
                    )}
                  </span>
                  {!isMobile && (
                    <span
                      className="col-resizer"
                      onMouseDown={(e) => startResize(e, col.key)}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.length === 0 ? (
              <tr>
                <td colSpan={visibleColumns.length} className="empty-row">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              sortedRows.map((row, i) => renderRow(row, i, hiddenCols))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
